import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Popconfirm, message, Space, Tag, Divider, Switch } from "antd";
import { Plus, Folder, File } from "lucide-react";
import dayjs from "dayjs";
import { useFolders, useFolderCreate, useFolderDelete, useDocFiles, useDocFileCreate, useDocFileDelete } from "../../../../api-hooks/documents";

export default function DocumentsTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [folderForm] = Form.useForm();
  const [fileForm] = Form.useForm();
  const [folderModal, setFolderModal] = useState(false);
  const [fileModal, setFileModal] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<number | null>(null);
  const [filePage, setFilePage] = useState(1);

  const { data: folders = [] } = useFolders(cid);
  const { data: fileData, isLoading: fLoading } = useDocFiles(cid, { folder_id: activeFolderId ?? undefined, page: filePage, limit: 50 });
  const files = (fileData as any)?.data ?? [];
  const fileTotal = (fileData as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const fCreate = useFolderCreate(cid, cb("Folder created.", () => setFolderModal(false)));
  const fDel = useFolderDelete(cid, { onSuccess: () => msgApi.success("Folder deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const dCreate = useDocFileCreate(cid, cb("Document added.", () => setFileModal(false)));
  const dDel = useDocFileDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const fileCols = [
    { title: "Name", dataIndex: "name", key: "n", render: (v: string) => <span className="flex items-center gap-1"><File size={13} className="text-slate-400" />{v}</span> },
    { title: "Type", dataIndex: "mime_type", key: "mt", render: (v: string) => v ? <Tag>{v.split("/")[1]?.toUpperCase()}</Tag> : "—" },
    { title: "Size", dataIndex: "file_size_kb", key: "sz", render: (v: number) => v ? `${v} KB` : "—" },
    { title: "Private", dataIndex: "is_private", key: "ip", render: (v: number) => <Tag color={v ? "red" : "green"}>{v ? "Private" : "Public"}</Tag> },
    { title: "Uploaded", dataIndex: "created_at", key: "ca", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expires", dataIndex: "expires_at", key: "ea", render: (v: string) => { if (!v) return "—"; const exp = dayjs(v).isBefore(dayjs()); return <Tag color={exp ? "red" : "blue"}>{dayjs(v).format("DD/MM/YY")}</Tag>; } },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => window.open(r.file_url, "_blank")}>View</Button>
        <Popconfirm title="Delete?" onConfirm={() => dDel.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div className="flex gap-4">
      {ctx}
      {/* Folder sidebar */}
      <div className="w-44 flex-shrink-0">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase">Folders</span>
          <Button size="small" icon={<Plus size={11} />} onClick={() => { setFolderModal(true); folderForm.resetFields(); }}>New</Button>
        </div>
        <div className="space-y-1">
          <div className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer text-sm ${activeFolderId === null ? "bg-violet-100 text-violet-700 font-medium" : "hover:bg-slate-100 text-slate-600"}`} onClick={() => setActiveFolderId(null)}>
            <Folder size={13} /> All Files
          </div>
          {(folders as any[]).map((f: any) => (
            <div key={f.id} className={`flex items-center justify-between gap-1 px-2 py-1 rounded cursor-pointer text-sm group ${activeFolderId === f.id ? "bg-violet-100 text-violet-700 font-medium" : "hover:bg-slate-100 text-slate-600"}`} onClick={() => setActiveFolderId(f.id)}>
              <span className="flex items-center gap-1 truncate"><Folder size={13} />{f.name}</span>
              <Popconfirm title="Delete folder?" onConfirm={e => { e?.stopPropagation(); fDel.mutate({ id: f.id }); }} okButtonProps={{ danger: true }}>
                <Button size="small" type="text" danger className="opacity-0 group-hover:opacity-100 !p-0 !h-auto" onClick={e => e.stopPropagation()}>×</Button>
              </Popconfirm>
            </div>
          ))}
        </div>
      </div>

      {/* Files list */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-end mb-2">
          <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setFileModal(true); fileForm.resetFields(); fileForm.setFieldsValue({ folder_id: activeFolderId, is_private: false }); }} style={{ background: "#4c1d95" }}>Add Document</Button>
        </div>
        <Table rowKey="id" dataSource={files} columns={fileCols} loading={fLoading} size="small" pagination={{ current: filePage, pageSize: 50, total: fileTotal, onChange: setFilePage }} />
      </div>

      {/* Folder Modal */}
      <Modal open={folderModal} onCancel={() => setFolderModal(false)} footer={null} destroyOnClose title="New Folder" width={360}>
        <Form form={folderForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => fCreate.mutate(vals)}>
          <Form.Item name="name" label="Folder Name" rules={[{ required: true }]}><Input /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={fCreate.isPending}>Create</Button>
            <Button onClick={() => setFolderModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* File Modal */}
      <Modal open={fileModal} onCancel={() => setFileModal(false)} footer={null} destroyOnClose title="Add Document" width={460}>
        <Form form={fileForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => dCreate.mutate({ ...vals, uploaded_by: 1 })}>
          <Form.Item name="name" label="Document Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="file_url" label="File URL" rules={[{ required: true }]}><Input placeholder="https://..." /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="mime_type" label="MIME Type"><Input placeholder="application/pdf" /></Form.Item>
            <Form.Item name="file_size_kb" label="Size (KB)"><Input type="number" /></Form.Item>
          </div>
          <Form.Item name="folder_id" label="Folder"><Select allowClear options={[{ value: null, label: "None" }, ...(folders as any[]).map((f: any) => ({ value: f.id, label: f.name }))]} /></Form.Item>
          <Form.Item name="is_private" label="Private" valuePropName="checked"><Switch /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={dCreate.isPending}>Save</Button>
            <Button onClick={() => setFileModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
